import {Component, effect, Inject, inject, Signal, TrackByFunction, ViewChild} from '@angular/core';
import {map, take} from 'rxjs/operators';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {BannerService} from '../../../banner/banner.service';
import {Apollo} from 'apollo-angular';
import {SourceDurations} from '../source-duration';
import {Airline, Mutation, StartWorkflowVodInput, TemplateCapabilities} from '../../../types-workflow';

import {START_WORKFLOW_VOD_DEFAULT, TEMPLATE_CAPABILITIES} from '../../../queries-workflow';
import {forkJoin} from 'rxjs';
import {Router} from '@angular/router';
import {isAudioOnlyLadder, LADDER_PRESETS} from '../encoding-ladder/encoding-ladder.model';
import {SchemaFormatSpecificComponent} from '../schema-form/schema-format-specific.component';
import {JsonSchema, parseSchema} from '../schema-form/fsd-schema';
import {BasicPoitemVodInfoComponent} from '../basics-poitem-vod-info/basic-poitem-vod-info.component';
import {clean_datetime} from '../../utils';
import {poNumberValidator} from '../../name-limits';
import {PO_DESTINATIONS, PoDestination} from '../../../target_systems';
import {Organization} from '../../../types-repository';
import {Po2024Service} from '../../../services/po-2024.service';
import {migrateLegacyFsd, VodEncodeSettings, VodEncodeSettingsService} from '../../../services/vod-encode-settings.service';

import {MessageFieldComponent} from '../../do-button/message-field.component';
import {reportInvalid, startEncodeLabel} from '../form-messages';
import {VOD_FIELD_HINTS, VOD_SECTIONS} from '../schema-form/section-headings';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {FormFooterComponent} from '../../form-footer/form-footer.component';
import {ContextService} from '../../../services/context.service';
import {ProcessService} from '../../../services/process.service';
import {Dialog} from '@angular/cdk/dialog';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import {OAuthService} from 'angular-oauth2-oidc';



/**
 * The settings with the options that need a video rendition switched off while the ladder is
 * audio-only: the template renders neither a watermark nor thumbnails without video, and fails the
 * job when asked to. The same object when nothing has to change.
 */
export function withoutVideoOnlyOptions(fsd: any): any {
  if (!fsd || !isAudioOnlyLadder(fsd.encoding_ladder) || (!fsd.watermark && !fsd.thumbs)) {
    return fsd;
  }
  return {...fsd, watermark: null, thumbs: false};
}

/** The `po_name` this form submits — the single place it is composed. */
function composePoName(form: AbstractControl): string {
  return `${form.get('airline')?.value ?? ''}_${(form.get('po_number')?.value ?? '').trim()}`;
}

@Component({
  selector: 'app-vod-default-po',
  templateUrl: './vod-default-po.component.html',
  styleUrls: ['./vod-default-po.component.css'],
  imports: [
    ReactiveFormsModule,
    BasicPoitemVodInfoComponent,
    SchemaFormatSpecificComponent,
    MessageFieldComponent,
    FormFooterComponent
]
})
export class VodDefaultPoComponent  {

  organization: Signal<Organization>;
  airlines: Signal<Airline[]>;
  poDestinations: PoDestination[] = []
  /**
   * Prefix of every element id in this form. Both VOD forms sit side by side in the folder page's
   * tabs, and ids are looked up document-wide (reportInvalid's focus scope, labels): with shared
   * ids a refused STARDUSTmark submit went to the VOD Default item and switched to its tab.
   */
  readonly formId = 'vod-default-po';

  poFormGroup: FormGroup;

  poItemTrackBy: TrackByFunction<FormGroup> = (index: number, poitem: FormGroup) => {
    return poitem.get('id').value;
  }

  /** Import and export feedback. Separate from the submit button's field, next to its own buttons. */
  @ViewChild('settingsMessages') settingsMessages: MessageFieldComponent;

  /** Set while the form is being written to on purpose, so the write is not stored as an edit. */
  private restoringSettings = false;

  /** The template this page encodes with. */
  static readonly VTK_TEMPLATE = 'vod-default-cmaf-abr-v2';

  /** Its format_specific_data schema (template_capabilities); null until loaded. */
  fsdSchema: JsonSchema | null = null;
  // Source video duration (seconds) per item source (`input_brefix`).
  private readonly sourceDurations = new SourceDurations(inject(Apollo));

  /** What the template delivers, in its own words (template_capabilities.description); null until loaded. */
  templateDescription: string | null = null;

  /** Whether the operator has changed an output option on this visit (see _persistSettings). */
  settingsEdited = false;

  constructor(
    private apollo: Apollo,
    @Inject('vod_default') protected poService: Po2024Service,
    private formBuilder: FormBuilder,
    private banner: BannerService,
    contextService: ContextService,
    private processService: ProcessService,
    private router: Router,
    private dialog: Dialog,
    private encodeSettings: VodEncodeSettingsService,
    oAuthService: OAuthService,
  ) {
    this.organization = contextService.organization;
    this.airlines = contextService.airlines;

    // The output options as this operator last left them, or the defaults on a first visit. Read
    // once and used for every seed below, so the two controls cannot disagree about where their
    // starting value came from.
    const settings = this.encodeSettings.loadOrDefaults();
    // Seeded from the logged-in user's own address, same as the IFE Workflow form -- the operator
    // submitting the PO is notified by default but can redirect it.
    const userEmail = oAuthService.getIdentityClaims()['email'];

    this.poFormGroup = formBuilder.group({
      airline: [null, Validators.required],
      po_number: [clean_datetime(new Date()), [Validators.required, poNumberValidator(composePoName)]],
      po_destination: [null, Validators.required],
      auto_publish: [true, Validators.required],
      // Optional: an empty value submits no notification at all, which the finalize/error
      // lambdas already treat as a normal, unremarkable case (see all_finalize_fail,
      // default_finalize_success, watermark_finalize_success -- all skip sending, not fail, when
      // email_notification is empty).
      email_notification: [userEmail, [Validators.email]],
      // Watermark, encryption and the ladder are one setting for the whole PO -- see the template
      // for why none of these is per item. Both are seeded from the stored settings rather than
      // from the defaults: they are how this operator encodes, which does not change between one
      // PO and the next, and re-picking a ladder every visit is what the persistence removes.
      // The ladder is one of these keys (the schema's ladder widget); the stored settings keep it
      // apart, so it is merged in here and split off again in _currentSettings.
      format_specific_data: [VodDefaultPoComponent.mergeSettings(settings)],
      poItems: formBuilder.array([]),
    });


    // The Project ID's limits are on the composed po_name, which also carries the airline.
    this.poFormGroup.controls['airline'].valueChanges.subscribe(() => {
      this.poFormGroup.controls['po_number'].updateValueAndValidity();
    });
    this.poService.getFormState().pipe(take(1)).subscribe(savedForm => {
      if (savedForm) {
        // The saved state is whatever shape the form had when it was stored, and the item shape has
        // changed (per-item format_specific_data and `thumbs` became PO-wide settings).
        // A saved item still carrying the old shape would build a FormGroup the template cannot
        // bind, and Angular would throw "Cannot find control with name: ..." on render. The
        // state is in-memory convenience, so normalise it rather than letting it break the page.
        // A state saved while `thumbs` was per item carries it on every entry, and the item form
        // has no such control any more. Dropping it silently would discard a choice the operator
        // made, so it is lifted to the PO instead: if any item asked for thumbnails, the PO does.
        // Items disagreeing is exactly the situation this move set out to remove.
        const savedThumbs = (savedForm.poItems ?? []).some((item: any) => item?.thumbs === true);
        savedForm = {
          ...savedForm,
          // Completed from the stored settings rather than the bare defaults, for the same reason
          // the controls are seeded from them: a key the saved state predates should fall back to
          // what this operator last chose, not to what a first-time visitor would see.
          format_specific_data: {
            ...VodDefaultPoComponent.mergeSettings(settings),
            ...migrateLegacyFsd(savedForm.format_specific_data ?? {}),
            // A state saved while the ladder was its own control.
            ...(savedForm.encoding_ladder ? {encoding_ladder: savedForm.encoding_ladder} : {}),
            ...(savedThumbs ? {thumbs: true} : {}),
          },
          poItems: (savedForm.poItems ?? []).map(({format_specific_data, thumbs, ...item}: any) => item),
        };
        savedForm.poItems.forEach(poItem => (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(poItem)));
        // setValue demands every control, so a form state saved before the ladder existed would
        // throw. Seed the new control's default and let the saved value win where it has one.
        const {encoding_ladder: _ladder, ...restored} = savedForm;
        this.poFormGroup.setValue(
          {
            email_notification: userEmail,
            ...restored,
          },
          {emitEvent: false})
      }
    });

    // Registered after the restore above, which patches with emitEvent: false and would otherwise
    // write back what it just read. Every later change to either card is stored as it happens:
    // there is no Save button, because a settings card nobody remembers to save is a settings card
    // that silently encodes last month's ladder.
    // An audio-only ladder switches watermark and thumbnails off (see withoutVideoOnlyOptions); the
    // corrected value comes back through here and is stored then.
    const fsdControl = this.poFormGroup.controls['format_specific_data'];
    fsdControl.valueChanges.subscribe(fsd => {
      const allowed = withoutVideoOnlyOptions(fsd);
      if (allowed !== fsd) {
        fsdControl.setValue(allowed);
        return;
      }
      this._persistSettings();
    });

    this.apollo.use('workflow').query<{ template_capabilities: TemplateCapabilities }>({
      query: TEMPLATE_CAPABILITIES,
      variables: {vtk_template: VodDefaultPoComponent.VTK_TEMPLATE},
    }).subscribe({
      next: r => {
        this.fsdSchema = parseSchema(r.data?.template_capabilities?.format_specific_data_schema);
        this.templateDescription = r.data?.template_capabilities?.description || null;
      },
      error: err => console.error(`template_capabilities(${VodDefaultPoComponent.VTK_TEMPLATE}) failed`, err),
    });

    effect(() => {
      this.poFormGroup.controls['airline'].valueChanges.subscribe(airline => {
        const legacyName = this.organization().legacy_name;
        this.poDestinations = PO_DESTINATIONS.hasOwnProperty(legacyName)
          ? [{display_name: legacyName, destination_id: legacyName}, {
            display_name: 'Default CDN',
            destination_id: 'vod'
          }]
          : [{display_name: 'Default CDN', destination_id: 'vod'}];
        this.poFormGroup.controls['po_destination'].setValue(this.poDestinations[0].destination_id)
      })
    });
    this.poService.poItemToAdd$.subscribe(data => {
      console.log('poItemToAdd$')
      if (data) {
        (this.poFormGroup.controls.poItems as FormArray).push(this.formBuilder.group(
          Object.assign(
            {},
            // Nothing per item any more: an item is its basics and nothing else. `thumbs` was the
            // last per-item output option and is now PO-wide, in the format-specific card.
            {basics: BasicPoitemVodInfoComponent.DEFAULTS},
            data)))
      }
    });
    this.poService.poItemToRemove$.subscribe(id => {
      if (id) {
        const index = (this.poFormGroup.controls.poItems as FormArray).controls.findIndex(poItem => poItem.get('id').value === id);
        (this.poFormGroup.controls.poItems as FormArray).removeAt(index);
      }
    });
    effect(() => {
      if (this.airlines().length === 1) {
        this.poFormGroup.controls['airline'].setValue(this.airlines()[0].iata_code);
      }
    })
  }



  compare_destination_id(o1: any, o2: any) {
    console.log(o1, o2)
    return o1?.destination_id === o2?.destination_id;
  }

  submitted = false;

  submit_po(button: DoButtonComponent) {
    try {
      this._submit_po(button)
    } catch (err) {
      // performAction() starts the spinner before emitting, so anything that throws on the way to
      // subscribe() leaves it spinning. Report it instead of stranding the button.
      console.error('submit_po failed', err)
      button.done(`Could not start the encode: ${err}`, {severity: 'cl-alert-danger'})
    }
  }

  private _submit_po(button: DoButtonComponent) {
    const organization = this.organization()
    if (!this.fsdSchema) {
      button.done('The template\'s settings are not loaded yet; try again in a moment.', {severity: 'cl-alert-danger'});
      return
    }
    if (!this.poFormGroup.valid) {
      this.submitted = true;
      const poItems = this.poFormGroup.controls.poItems as FormArray
      const invalidIdx = poItems.controls.findIndex(c => c.invalid)
      this.poFormGroup.markAllAsTouched();
      // Scroll to and focus the first invalid field of this form only (its ids are unique per form).
      reportInvalid(button, this.poFormGroup, invalidIdx >= 0 ? `${this.formId}-item-${invalidIdx}` : this.formId);
      return
    }
    // PO-wide, same as format_specific_data/encoding_ladder below -- sent on every item's mutation.
    // Optional field: a blank value submits no notification at all.
    const emails = this.poFormGroup.value.email_notification?.split(',').map(e => e.trim()).filter(e => e !== '') || []
    const workflows = []
    for (const poItem of this.poFormGroup.value.poItems) {
      workflows.push(this.apollo.use('workflow').mutate<Mutation>({
        mutation: START_WORKFLOW_VOD_DEFAULT,
        variables: {
          input_brefix: poItem.basics.input_brefix,
          po_item_id: poItem.basics.po_item_id.trim(),
          po_name: composePoName(this.poFormGroup),
          po_destination: this.poFormGroup.value.po_destination,
          email_notification: emails,
          // The full name, not the 'cmaf-abr' shorthand: default_generate_vtk_script prefixes
          // anything without a 'vod-default-' prefix, and the shorthand resolved to v1 -- which
          // has no encoding_ladder at all and silently ignores the key, since top-level
          // format_specific_data keys are not validated. v2 is the one that reads the ladder.
          vtk_template: VodDefaultPoComponent.VTK_TEMPLATE,
          auto_publish: this.poFormGroup.value.auto_publish,
          // One PO-wide watermark/encryption/ladder/thumbnail setting, shared by every item: the
          // schema form's value, already pruned to the keys that apply and with the ladder
          // normalised (and its DRM tiers dropped when unencrypted).
          format_specific_data: JSON.stringify(this.poFormGroup.value.format_specific_data)
        } satisfies StartWorkflowVodInput

      }).pipe(
        map(d => ({
          process: d.data.start_workflow_vod_default,
          po_item_id: poItem.basics.po_item_id.trim()
        }))))
    }

    // forkJoin over an empty array completes without ever emitting, so `next` -- and with it
    // button.done() -- would never run: the button would spin forever with no request, no error
    // and nothing in the console. Nothing else guards this; the button has no disabled state.
    if (!workflows.length) {
      button.done('Add at least one PO item before starting an encode.', {severity: 'cl-alert-warning'});
      return
    }

    forkJoin(workflows).subscribe(
      {
        next: wfs => {
          button.done('');

          // Read before the reset below, which is what clears the form.
          const kept = this._currentSettings();

          const ref = this.dialog.open(ConfirmDialogComponent, {
            data: {
              headline: 'Encoding started',
              message: `All encode${wfs.length === 1 ? '' : 's'} (${wfs.length} item${wfs.length === 1 ? '' : 's'}) started successfully.`,
            }
          });

          ref.closed.subscribe(() => {
            this.submitted = false;
            (this.poFormGroup.controls.poItems as FormArray).clear();
            this.poFormGroup.reset({
              airline: this.airlines().length === 1 ? this.airlines()[0].iata_code : null,
              po_number: clean_datetime(new Date()),
              po_destination: this.poFormGroup.value.po_destination,
              auto_publish: true,
              // Listed explicitly: reset() nulls any control it is not given, which would hand the
              // format-specific component a null value and drop the ladder. Carried over rather
              // than reset to the defaults -- the items are what a submitted PO consumes, the
              // output options are how this operator encodes, and the next PO wants them again.
              format_specific_data: VodDefaultPoComponent.mergeSettings(kept),
              poItems: [],
            });
          });

          wfs.forEach(workflow => {
            this.processService.observeProcess(workflow.process, {
              successMessage: `${workflow.po_item_id} encoded.`,
              failureMessage: `Failed to encode ${workflow.po_item_id}.`,
              link: ['/#/o', organization.id, 'workflows',
                this.poFormGroup.value.airline,
                `${this.poFormGroup.value.airline}_${this.poFormGroup.value.po_number}`].join('/')
            })
          })
        },
        error: err => {
          button.done('Workflow submission failed: ' + err, {severity: 'cl-alert-danger'});
        }
      }
    )

  }

  /**
   * The PO's source duration for the watermark auto-selection: the settings are PO-wide, so the
   * longest known item source decides. null while none is known.
   */
  get poSourceDurationSeconds(): number | null {
    const known = this.poItems.controls
      .map(c => this.sourceDurations.of(c.value?.basics?.input_brefix))
      .filter((d): d is number => typeof d === 'number');
    return known.length ? Math.max(...known) : null;
  }

  readonly startEncodeLabel = startEncodeLabel;
  /** Content Protection over Watermark and DRM: VOD only. */
  readonly sections = VOD_SECTIONS;
  /** The Onboard availability window as days: VOD only. */
  readonly fieldHints = VOD_FIELD_HINTS;

  get poItems() {
    return this.poFormGroup.get('poItems') as FormArray<FormGroup>;
  }

  /** Stored settings → the form's value: the ladder is one of the format_specific_data keys. */
  static mergeSettings(settings: VodEncodeSettings): any {
    return {...(settings.format_specific_data ?? {}), encoding_ladder: settings.encoding_ladder};
  }

  // ── Encode settings: persistence, export, import ───────────────────────────

  /** Whether "Forget saved settings" has anything to forget. */
  get hasStoredSettings(): boolean {
    return this.encodeSettings.hasStored();
  }

  /** Write the current output options to a JSON file the operator keeps or shares. */
  exportSettings(): void {
    const now = new Date();
    const blob = new Blob([VodEncodeSettingsService.toFile(this._currentSettings(), now)],
      {type: 'application/json'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = VodEncodeSettingsService.fileName(now);
    a.click();
    window.URL.revokeObjectURL(url);
    this._settingsMessage('Encode settings exported.', 'cl-alert-info');
  }

  /**
   * Read a settings file back into the two cards.
   *
   * The file input is reset afterwards so picking the same file twice still fires a change event --
   * an import that silently does nothing the second time is worse than no import at all.
   */
  importSettings(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }
    file.text()
      .then(text => {
        this.settingsEdited = true;
        this._applySettings(VodEncodeSettingsService.fromFile(text));
        this._settingsMessage(`Encode settings loaded from ${file.name}.`, 'cl-alert-info');
      })
      .catch(err => this._settingsMessage(`Could not import ${file.name}: ${err.message ?? err}`, 'cl-alert-danger'));
  }

  /**
   * Back to the defaults, and stop restoring anything on the next visit.
   *
   * The stored entry is removed rather than overwritten with the defaults, so a later version whose
   * defaults have moved on is not pinned to today's by an operator who once pressed this. The
   * subsequent valueChanges would store them again -- which is why `_persistSettings` is skipped
   * while this runs.
   */
  resetSettings(): void {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        headline: 'Forget saved encode settings',
        message: 'The watermark, encryption and ladder settings go back to their defaults, and '
          + 'nothing is restored the next time you open this page. PO items are untouched.',
        actionName: 'Forget',
        action: () => {
          this.restoringSettings = true;
          this._applySettings(VodEncodeSettingsService.defaults());
          this.encodeSettings.clear();
          this.settingsEdited = false;
          this.restoringSettings = false;
          this._settingsMessage('Saved encode settings forgotten.', 'cl-alert-info');
        },
      },
    });
  }

  /** The two PO-wide output settings as they currently stand in the form. */
  private _currentSettings(): VodEncodeSettings {
    const {encoding_ladder, ...rest} = this.poFormGroup.controls['format_specific_data'].value ?? {};
    return {
      format_specific_data: rest,
      encoding_ladder: encoding_ladder ?? LADDER_PRESETS[0].ladder,
    };
  }

  /**
   * Write settings into the two cards.
   *
   * `setValue` per control rather than `patchValue` on the group: each card is a
   * ControlValueAccessor, and writing the control is what runs its `writeValue` and redraws it.
   * The events it emits are what stores the imported settings, so an import survives a reload the
   * same way a hand edit does.
   */
  private _applySettings(settings: VodEncodeSettings): void {
    this.poFormGroup.controls['format_specific_data'].setValue(VodDefaultPoComponent.mergeSettings(settings));
  }

  /**
   * Store the settings, unless there is nothing to store yet.
   *
   * The schema form emits its prefilled value on its own, so saving on every event would write an
   * entry on a first visit where the operator has changed nothing -- pinning them to today's
   * defaults, which is the one thing "Forget saved settings" exists to undo. So while nothing is
   * stored, only an operator's edit (or an import) is stored. Once something is stored, every later
   * change is.
   */
  private _persistSettings(): void {
    if (this.restoringSettings) {
      return;
    }
    if (!this.encodeSettings.hasStored() && !this.settingsEdited) {
      return;
    }
    this.encodeSettings.save(this._currentSettings());
  }

  private _settingsMessage(message: string, severity: 'cl-alert-danger' | 'cl-alert-warning' | 'cl-alert-info'): void {
    this.settingsMessages?.setMessage(message, severity, severity === 'cl-alert-danger' ? -1 : 5000);
  }

  ngOnDestroy(): void {
    this.poService.setFormState(this.poFormGroup.value)
  }

}
